import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useCustomerStore } from '@/stores/customer-store';
import { useDevicesStore } from '@/stores/devices-store';
import { Forward, Plus, User, Users } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Import Components
import { AddProfileDialog } from '@/components/forward/add-profile-dialog';
import { AddRecipientDialog } from '@/components/forward/add-recipient-dialog';
import { ProfileDetailView } from '@/components/forward/profile-detail-view';
import { RecipientDetailView } from '@/components/forward/recipient-detail-view';

export default function ForwardPageNew() {
    const { t } = useTranslation();
    const { currentDevice } = useDevicesStore();
    const {
        callhandlingProfiles,
        forwardRecipients,
        loadCallHandlingProfiles,
        loadForwardRecipients,
        selectedForwardProfileId,
        selectedForwardRecipientId,
        forwardViewType,
        clearForwardSelection
    } = useCustomerStore();
    const { toast } = useToast();

    const [showAddRecipientDialog, setShowAddRecipientDialog] = useState(false);
    const [showAddProfileDialog, setShowAddProfileDialog] = useState(false);

    // Find selected profile or recipient based on store state
    const selectedProfile = useMemo(() => {
        if (!selectedForwardProfileId) return null;
        return callhandlingProfiles.find(p => p.id === selectedForwardProfileId) || null;
    }, [selectedForwardProfileId, callhandlingProfiles]);

    const selectedRecipient = useMemo(() => {
        if (!selectedForwardRecipientId) return null;
        return forwardRecipients.find(r => r.id === selectedForwardRecipientId) || null;
    }, [selectedForwardRecipientId, forwardRecipients]);

    // Load data on mount
    useEffect(() => {
        if (!currentDevice) {
            return;
        }

        const loadData = async () => {
            try {
                await Promise.all([
                    loadForwardRecipients(),
                    loadCallHandlingProfiles()
                ]);
            } catch (error) {
                console.error('Failed to load forward data:', error);
                toast({
                    title: t('error.title'),
                    description: t('forwardPage.loadError'),
                    variant: 'destructive'
                });
            }
        };

        loadData();
    }, [currentDevice, loadCallHandlingProfiles, loadForwardRecipients, toast]);

    // Render empty state when no profile or recipient is selected
    const renderEmptyState = () => (
        <div className="flex h-full items-center justify-center">
            <div className="text-center max-w-md p-8">
                <div className="mx-auto w-16 h-16 bg-brand-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Forward className="h-8 w-8 text-brand-primary" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('navigation.forward')}</h2>
                <p className="text-muted-foreground mb-6">
                    {t('forwardPage.emptyDescription')}
                </p>
                <div className="flex gap-3 justify-center">
                    <Button
                        variant="outline"
                        onClick={() => setShowAddProfileDialog(true)}
                        className="border-brand-primary text-brand-primary hover:bg-brand-primary/10"
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        {t('sidebar.newProfile')}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => setShowAddRecipientDialog(true)}
                        className="border-brand-primary text-brand-primary hover:bg-brand-primary/10"
                    >
                        <Plus className="mr-1 h-4 w-4" />
                        {t('sidebar.newRecipient')}
                    </Button>
                </div>

                {/* Quick stats */}
                <div className="mt-8 flex justify-center gap-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-2 text-gray-600">
                            <Users className="h-4 w-4" />
                            <span className="text-2xl font-bold text-gray-900">{callhandlingProfiles.length}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{t('sidebar.profiles')}</p>
                    </div>
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-2 text-gray-600">
                            <User className="h-4 w-4" />
                            <span className="text-2xl font-bold text-gray-900">{forwardRecipients.length}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{t('sidebar.recipients')}</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="flex h-full flex-col bg-gray-50">
            {/* Content Area */}
            <div className="flex-1 overflow-y-auto">
                {/* Show profile detail if profile is selected */}
                {forwardViewType === 'profile' && selectedProfile && (
                    <ProfileDetailView
                        profile={selectedProfile}
                        recipients={forwardRecipients}
                        onUpdate={loadCallHandlingProfiles}
                    />
                )}

                {/* Show recipient detail if recipient is selected */}
                {forwardViewType === 'recipient' && selectedRecipient && (
                    <RecipientDetailView
                        recipient={selectedRecipient}
                        onUpdate={loadForwardRecipients}
                        onDelete={() => {
                            clearForwardSelection();
                            loadForwardRecipients();
                        }}
                    />
                )}

                {/* Show empty state if no selection */}
                {forwardViewType === 'none' && renderEmptyState()}

                {/* Show not found if profile ID doesn't match */}
                {forwardViewType === 'profile' && !selectedProfile && callhandlingProfiles.length > 0 && (
                    <div className="flex h-full items-center justify-center">
                        <div className="text-center">
                            <p className="text-muted-foreground">{t('forwardPage.profileNotFound')}</p>
                            <Button
                                variant="link"
                                onClick={() => clearForwardSelection()}
                                className="mt-2 text-brand-primary"
                            >
                                {t('global.button.backToOverview')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Show not found if recipient ID doesn't match */}
                {forwardViewType === 'recipient' && !selectedRecipient && forwardRecipients.length > 0 && (
                    <div className="flex h-full items-center justify-center">
                        <div className="text-center">
                            <p className="text-muted-foreground">{t('forwardPage.recipientNotFound')}</p>
                            <Button
                                variant="link"
                                onClick={() => clearForwardSelection()}
                                className="mt-2 text-brand-primary"
                            >
                                {t('global.button.backToOverview')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Dialogs */}
            <AddRecipientDialog
                open={showAddRecipientDialog}
                onOpenChange={setShowAddRecipientDialog}
                onSuccess={loadForwardRecipients}
            />

            <AddProfileDialog
                open={showAddProfileDialog}
                onOpenChange={setShowAddProfileDialog}
                onSuccess={loadCallHandlingProfiles}
                recipients={forwardRecipients}
            />
        </div>
    );
}
